import { inject, Injectable } from '@angular/core';
import { MembershipApiService } from '@features/membership/services/membership-api/membership-api.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { SentryService } from '@rd-lib/sentry/sentry.service';
import { RCV2_MEMBERSHIP_FAILED_DIALOG } from '@rd/core/tokens/redeem-cash-v2.token';
import { DialogService } from '@rd/popup/services/dialog/dialog.service';
import { HttpError, Membership } from '@rd/types';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { RcV2MembershipSuccessDialogComponent } from '../../components/membership-success-dialog/membership-success-dialog.component';
import { RedeemCashSettingService } from '../../services/redeem-cash-setting/redeem-cash-setting.service';
import * as actions from '../actions/membership.actions';

@Injectable()
export class MembershipEffects {
  private rcv2MembershipFailedDialogComponentType = inject(
    RCV2_MEMBERSHIP_FAILED_DIALOG,
  );

  constructor(
    private actions$: Actions,
    private membershipApiService: MembershipApiService,
    private sentryService: SentryService,
    private dialogService: DialogService,
    private translateService: TranslateService,
    private redeemCashSettingService: RedeemCashSettingService,
  ) {}

  loadMemberships$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.MembershipActionTypes.LOAD_MEMBERSHIPS),
      switchMap(() => {
        return this.membershipApiService.getAllMemberships().pipe(
          map((memberships: Membership[]) => {
            return new actions.LoadMembershipsSuccess(memberships);
          }),
          catchError((error: HttpError) =>
            of(new actions.LoadMembershipsFail(error)),
          ),
        );
      }),
    ),
  );

  createMembership$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.MembershipActionTypes.CREATE_CASHBACK_MEMBERSHIP),
      switchMap((action: actions.CreateMembership) => {
        return this.membershipApiService.createMembership(action.payload).pipe(
          switchMap((res) => {
            this.dialogService.open(RcV2MembershipSuccessDialogComponent, {
              data: {},
              id: 'rcv2-membership-success-dialog',
              screenMode: 'center-md',
              ariaLabel: this.translateService.instant(
                'redeem_cash_v2.membership_success_dialog.title',
              ),
            });

            return [
              new actions.CreateMembershipSuccess(res),
              new actions.LoadMemberships(), // Upon creating a membership, re-dispatch LoadMemberships to update Memberships entirely again
            ];
          }),
          catchError((error: HttpError) =>
            of(new actions.CreateMembershipFail(error)),
          ),
        );
      }),
    ),
  );

  createMembershipFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.MembershipActionTypes.CREATE_MEMBERSHIP_FAIL),
        tap((action: actions.CreateMembershipFail) => {
          this.dialogService.open(
            this.rcv2MembershipFailedDialogComponentType,
            {
              data: {},
              id: 'rcv2-membership-failed-dialog',
              screenMode: 'center-md',
              ariaLabel: this.translateService.instant(
                'redeem_cash_v2.membership_failed_dialog.title',
              ),
            },
          );
        }),
      ),
    { dispatch: false },
  );

  updateMembership$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.MembershipActionTypes.UPDATE_MEMBERSHIP),
      switchMap((action: actions.UpdateMembership) => {
        return this.membershipApiService
          .updateMembership(action.payload.updateMembershipData)
          .pipe(
            switchMap((res) => {
              if ('number' in action.payload.updateMembershipData.membership) {
                {
                  this.dialogService.open(
                    RcV2MembershipSuccessDialogComponent,
                    {
                      data: {},
                      id: 'rcv2-membership-success-dialog',
                      screenMode: 'center-md',
                      ariaLabel: this.translateService.instant(
                        'redeem_cash_v2.membership_success_dialog.title',
                      ),
                    },
                  );
                }
              }

              return [
                new actions.UpdateMembershipSuccess(res),
                new actions.LoadMemberships(),
              ];
            }),
            catchError((error: HttpError) =>
              of(new actions.UpdateMembershipFail(error)),
            ),
          );
      }),
    ),
  );

  updateMembershipFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.MembershipActionTypes.UPDATE_MEMBERSHIP_FAIL),
        tap((action: actions.UpdateMembershipFail) => {
          this.dialogService.open(
            this.rcv2MembershipFailedDialogComponentType,
            {
              data: {},
              id: 'rcv2-membership-failed-dialog',
              screenMode: 'center-md',
              ariaLabel: this.translateService.instant(
                'redeem_cash_v2.membership_failed_dialog.title',
              ),
            },
          );
        }),
      ),
    { dispatch: false },
  );
}
